/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  getContentDescription,
  getContentImage,
  getContentTitle,
  usePlayer,
} from "@packages/media";
import type { QueueManagedItem } from "@packages/sdk";
import { forwardRef, useCallback } from "react";
import type { DragOptions } from "react-aria";
import { DragPreview } from "react-aria";

import {
  constantSemanticColors,
  numericPercentages,
  spacing,
} from "../../../../../../global/emotion";
import { MediaMoreMenu } from "../../../../../components/_base/content/content/ContentMoreMenu/MediaMoreMenu.emotion";
import { ContentSmall } from "../../../../../components/_base/content/content/ContentSmall/ContentSmall.emotion";
import { VoxIcon } from "../../../../../components/_base/icons/VoxIcon/VoxIcon.emotion";
import { useQueueItem } from "../../../../hooks/useQueueItem.emotion";
import { QueueHandle } from "../../controls/QueueHandle/QueueHandle.emotion";

const defaultStyle: Interpolation = {
  padding: `${spacing.ms} ${spacing.m}`,
  scrollSnapAlign: "none",
  width: numericPercentages[100],
};
const voxStyle: Interpolation = {
  left: numericPercentages[50],
  position: "absolute",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
};
const ghostStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  filter: "saturate(0.5)",
};
const clickableStyle: Interpolation = {
  cursor: "pointer",
};

export type QueueItemProps = Pick<
  DragOptions,
  "onDragStart" | "onDragMove" | "onDragEnd"
> & {
  variant: "past" | "current" | "future" | "repeat";
  item: QueueManagedItem | null;
};

export const QueueItem = forwardRef<HTMLDivElement, QueueItemProps>(
  ({ onDragStart, onDragMove, onDragEnd, variant, item }, ref) => {
    const getItems = useCallback(
      () => [
        {
          "text/json": JSON.stringify(item),
        },
      ],
      [item],
    );

    const { handleProps, handleCSS, containerProps, previewRef } = useQueueItem(
      {
        onDragStart,
        onDragMove,
        onDragEnd,
        getItems,
      },
    );
    const analytics = useAnalytics();

    const player = usePlayer();

    if (!item) {
      throw "Tried to include a queue item with no content";
    }

    let rightAction = null;

    if (item.type === "prayer") {
      if (variant === "current") {
        rightAction = <MediaMoreMenu key="rightAction" content={item} />;
      } else if (variant === "future") {
        rightAction = (
          <QueueHandle key="rightAction" css={handleCSS} {...handleProps} />
        );
      }
    }

    return (
      <>
        <ContentSmall
          ref={ref}
          key={item.uuid}
          size={variant === "current" ? "s" : "xs"}
          imgSrc={getContentImage(item)}
          imgOverlay={
            variant === "current" ? (
              <VoxIcon playing={!!player && !player.paused} css={voxStyle} />
            ) : null
          }
          title={getContentTitle(item)}
          description={getContentDescription(item)}
          rightActions={[rightAction]}
          disabled={variant !== "future" || item.type === "radio_song"}
          onClick={() => {
            if (variant === "future" && item.type !== "radio_song") {
              analytics.track({
                event: HallowAnalyticsEvent.TappedQueue,
                properties: {
                  content_type: "prayer",
                  content_id: item.prayer.id,
                },
              });
              return player.goToQueueId(item.uuid);
            }
            return Promise.resolve();
          }}
          {...(variant === "future" ? containerProps : {})}
          css={[
            defaultStyle,
            variant === "repeat"
              ? ghostStyle
              : variant === "future" && item.type !== "radio_song"
                ? clickableStyle
                : null,
          ]}
        />
        {variant === "future" ? (
          <DragPreview
            ref={previewRef}
            key={`${item.selected_audio.id}_preview`}
          >
            {(items) => {
              const itm = JSON.parse(items[0]["text/json"]) as QueueManagedItem;
              return <div>{getContentTitle(itm)}</div>;
            }}
          </DragPreview>
        ) : null}
      </>
    );
  },
);

QueueItem.displayName = "QueueItem";
